/**
 * HTTP user service — security-service `/users`, `/roles` and `/invitations`
 * endpoints (proxied by the API Gateway under `/api/v1`).
 *
 * Tenant scoping is derived server-side from the auth token in real mode, so we
 * never pass `tenant_id` on the wire.
 *
 * Contract notes (verified against taxhive-security-service):
 *  - Create requires `username` + `password` (+ `user_category`); the role is a
 *    SEPARATE assignment (`POST /users/{id}/roles` with a role UUID), so create
 *    is a two-step flow: create the user, then assign the role.
 *  - Update is `PUT /users/{id}` (NOT PATCH) and only accepts
 *    `email | full_name | user_category | is_active | is_verified` — there is no
 *    `phone` column on users server-side, so phone edits do not round-trip live.
 *  - Activate/deactivate are dedicated endpoints, not an `is_active` PATCH.
 *  - Invitations live at `/invitations` (not `/users/invitations`). As of this
 *    writing the gateway does not proxy `/invitations` and the backend returns
 *    501/stubs, so invitations are functional in mock mode only. The paths below
 *    are contract-correct for when the backend lands.
 */

import { apiClient, toServiceError } from '@/services/http/client'
import type { UserService } from './users.service'
import type {
  User,
  UserRole,
  UserPreferences,
  CreateUserInput,
  UpdateUserInput,
  UserInvitation,
  InviteUserInput,
} from '@/types/user.types'
import {
  mapUserFromApi,
  mapUserListFromApi,
  mapInvitationFromApi,
  mapInvitationListFromApi,
  deriveUsername,
} from './mappers'
import { resolveAssignableRole, resolveRole, roleCategory } from '@/security/accessContract'

const DEFAULT_PAGE_SIZE = 100

/**
 * Map a UI role to the security-service `user_category`. The backend only knows
 * three categories; the granular role (partner/assistant/…) is captured by the
 * separate role assignment, not the category.
 */
function categoryForRole(role: UserRole): string {
  return roleCategory(role)
}

/**
 * Cache of role_name → role UUID, fetched once from `GET /roles`. The seeded
 * role names (owner/admin/partner/assistant/staff/client) match `UserRole`
 * 1:1, so a direct lookup is sufficient.
 */
let rolesCachePromise: Promise<Map<string, string>> | null = null

async function getRoleIdMap(): Promise<Map<string, string>> {
  if (!rolesCachePromise) {
    rolesCachePromise = apiClient
      .get('/roles', { params: { page: 1, page_size: DEFAULT_PAGE_SIZE } })
      .then((res) => {
        const items = (res.data?.items ?? res.data ?? []) as Array<{
          id?: string
          role_name?: string
        }>
        const map = new Map<string, string>()
        for (const r of items) {
          if (r.role_name && r.id) map.set(r.role_name, r.id)
        }
        return map
      })
      .catch((err) => {
        // Don't poison the cache on a transient failure.
        rolesCachePromise = null
        throw err
      })
  }
  return rolesCachePromise
}

async function resolveRoleId(role: UserRole, allowUnresolved = false): Promise<string | null> {
  const map = await getRoleIdMap()
  const resolution = resolveRole(role)
  const effectiveRole = allowUnresolved
    ? resolution.effectiveRole
    : resolveAssignableRole(role)
  return effectiveRole ? (map.get(effectiveRole) ?? null) : null
}

export function createHttpUserService(): UserService {
  return {
    async list(): Promise<User[]> {
      try {
        const res = await apiClient.get('/users', {
          params: { page: 1, page_size: DEFAULT_PAGE_SIZE },
        })
        return mapUserListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async create(input: CreateUserInput, tenantId: string): Promise<User> {
      try {
        const canonicalRole = resolveAssignableRole(input.role) ?? input.role
        const res = await apiClient.post('/users', {
          username: deriveUsername(input.email),
          email: input.email,
          password: input.password,
          full_name: input.fullName,
          user_category: categoryForRole(input.role),
          role: canonicalRole,
          // Admin-created accounts are pre-verified (no email round-trip).
          is_verified: true,
        })
        const created = mapUserFromApi(res.data)

        // Step 2: assign the requested role (idempotent fallback).
        try {
          const roleId = await resolveRoleId(input.role)
          if (roleId) {
            await apiClient.post(`/users/${created.userId}/roles`, { role_id: roleId })
          }
        } catch (err) {
          // If already assigned by POST /users, continue; otherwise log warning.
          console.warn('Role assignment step warning:', err)
        }

        // The create response has no tenant/role yet; fill them from what we know.
        return { ...created, role: resolveAssignableRole(input.role) ?? 'unrecognized', tenantId }
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async update(userId: string, updates: UpdateUserInput): Promise<Partial<User>> {
      try {
        // Only send fields the security-service `UserUpdate` schema accepts.
        const body: Record<string, unknown> = {}
        if (updates.fullName !== undefined) body.full_name = updates.fullName
        if (updates.isActive !== undefined) body.is_active = updates.isActive
        if (updates.preferences !== undefined) body.preferences = updates.preferences
        const res = await apiClient.put(`/users/${userId}`, body)
        return mapUserFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async remove(userId: string, reason = 'User requested deletion'): Promise<void> {
      try {
        await apiClient.delete(`/users/${userId}`, { headers: { 'X-Deletion-Reason': reason } })
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async setActive(userId: string, isActive: boolean): Promise<Partial<User>> {
      try {
        const action = isActive ? 'activate' : 'deactivate'
        const res = await apiClient.post(`/users/${userId}/${action}`)
        return mapUserFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async updateMe(updates: UpdateUserInput): Promise<Partial<User>> {
      try {
        // PUT /users/me accepts full_name | email | preferences (self-service).
        const body: Record<string, unknown> = {}
        if (updates.fullName !== undefined) body.full_name = updates.fullName
        if (updates.preferences !== undefined) body.preferences = updates.preferences
        const res = await apiClient.put('/users/me', body)
        return mapUserFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async updatePreferences(preferences: UserPreferences): Promise<UserPreferences> {
      try {
        const res = await apiClient.put('/users/me', { preferences })
        // The response echoes the persisted blob; fall back to what we sent.
        return (res.data?.preferences as UserPreferences) ?? preferences
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async assignRole(
      userId: string,
      role: UserRole,
      oldRole?: UserRole
    ): Promise<Partial<User>> {
      try {
        const roleId = await resolveRoleId(role)
        if (!roleId) throw new Error(`Unknown role: ${role}`)

        // Remove the previous role first so the user ends up with a single role.
        if (oldRole && oldRole !== role) {
          const oldRoleId = await resolveRoleId(oldRole, true)
          if (oldRoleId) {
            try {
              await apiClient.delete(`/users/${userId}/roles/${oldRoleId}`)
            } catch {
              /* old role may already be gone; ignore */
            }
          }
        }

        await apiClient.post(`/users/${userId}/roles`, { role_id: roleId })
        return { role: resolveAssignableRole(role) ?? 'unrecognized' }
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async listInvitations(): Promise<UserInvitation[]> {
      try {
        const res = await apiClient.get('/invitations', {
          params: { status_filter: 'pending' },
        })
        return mapInvitationListFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async invite(input: InviteUserInput): Promise<UserInvitation> {
      try {
        const res = await apiClient.post('/invitations', {
          email: input.email,
          role: resolveAssignableRole(input.role),
        })
        return mapInvitationFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async cancelInvitation(invitationId: string): Promise<void> {
      try {
        await apiClient.delete(`/invitations/${invitationId}`)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async resendInvitation(invitationId: string): Promise<{ expiresAt: string }> {
      try {
        const res = await apiClient.post(`/invitations/${invitationId}/resend`)
        const expiresAt: string =
          res.data?.expires_at ?? res.data?.expiresAt ?? new Date().toISOString()
        return { expiresAt }
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
